import Papa from 'papaparse';
import { IAlternativeCompany, IImportedCompany, IScoredUpload, IScoringGridCompany } from '../types/scoring-company';
import { displayCompanyIfNotDeleteMe, parseCompanyCode, parseCompanyName, parseCompanyCountry } from '../utils/bulk-loader-functions';

export const downloadFile = (csv: string, fileName: string) => {
  // Prepend UTF-8 Byte Order Mark (BOM) to help ensure correct encoding in applications like Microsoft Excel
  const csvWithBom = '\uFEFF' + csv;
  const blob = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  window.URL.revokeObjectURL(url);
};

export const createTemplate = () => {
  const templateData = [
    ['email', 'target_user_id', 'fcode', 'ticker_symbol', 'user_company', 'country', 'list_name', 'list_email', 'comment'],
    [
      'Required, including when target_user_id is supplied -- email address of portfolio we are adding',
      'Optional FSS user ID. Use only to select an exact existing user account when the email is ambiguous.',
      'exact match to fcode',
      'exact match to ticker_symbol',
      'approximate match of company name',
      'optional - country -- only populate if you are sure of country',
      'if field is complete - will add companies to existing list, or will create a new list if none exists and will add companies to that new list',
      'if email listed here is different to that in Column A, then company added to account and list for this email address as well as email address in Column A',
      'at least one of fcode, ticker_symbol or user_company must be populated',
    ],
    ['peter.lieder@oliverwyman.com', '', 'uor', '', '', '', '', '', 'fcode match'],
    ['francis.julian@oliverwyman.com', '', '', 'mmc', '', '', '', '', 'ticker symbol match'],
    ['francis.julian@oliverwyman.com', '', '', '', 'US30231G1022', '', '', '', 'ISIN match'],
    ['francis.julian@oliverwyman.com', '', '', '', 'Apple Creek Acquisition', '', '', '', 'Three word match'],
    [
      'francis.julian@oliverwyman.com',
      '',
      '',
      '',
      'Apple',
      '',
      '',
      '',
      'Finds Apple, Inc United States as this is the main company with the most articles',
    ],
    ['francis.julian@oliverwyman.com', '', '', '', 'Apple', 'Canada', '', '', 'The Canadian version of Apple'],
    ['francis.julian@oliverwyman.com', '', '', '', 'The Boeing Company', '', '', '', 'Ignores The and Company'],
    ['francis.julian@oliverwyman.com', '', '', '', 'Néstle SA', '', '', '', 'Ignores accent which is in wrong e; Ignores SA'],
    [
      'francis.julian@oliverwyman.com',
      '',
      '',
      '',
      'Spirit Aerospace',
      '',
      '',
      '',
      'Second suggestion in drop down is Spirit Aerosystems which is probably what we want',
    ],
  ];
  return Papa.unparse(templateData, { quotes: true });
};

const parseTargetUserId = (targetUserId: string | number | undefined, rowNumber: number): number | undefined => {
  if (targetUserId === undefined || targetUserId === null || String(targetUserId).trim() === '') return undefined;

  const value = String(targetUserId).trim();
  if (!/^[1-9]\d*$/.test(value) || !Number.isSafeInteger(Number(value))) {
    throw new Error(`Row ${rowNumber}: target_user_id must be a positive integer`);
  }

  return Number(value);
};

export const parseImportFields = (importedCompanies: IImportedCompany[]): IScoringGridCompany[] => {
  // default was a 0 based index which caused problems at the database level
  let idCounter = 1;
  return importedCompanies?.flatMap((imported_company: IImportedCompany, index: number) => {
    const target_user_id = parseTargetUserId(imported_company.target_user_id, index + 2);
    const baseCompany: IScoringGridCompany = {
      id: idCounter++,
      ...(target_user_id === undefined ? {} : { target_user_id }),
      email: (imported_company.email ?? '').toLocaleLowerCase(),
      fcode: imported_company.fcode ?? '',
      ticker_symbol: imported_company.ticker_symbol ?? '',
      user_company: imported_company.user_company ?? '',
      country: imported_company.country ?? '',
      list_name: imported_company.list_name ?? '',
      selected_company: '',
      alternative_companies: [],
      upload_status: '',
    };
    if (imported_company.list_email) {
      const listEmailCompany: IScoringGridCompany = {
        id: idCounter++,
        email: imported_company.list_email.toLocaleLowerCase(),
        fcode: imported_company.fcode ?? '',
        ticker_symbol: imported_company.ticker_symbol ?? '',
        user_company: imported_company.user_company ?? '',
        country: imported_company.country ?? '',
        list_name: imported_company.list_name ?? '',
        selected_company: '',
        alternative_companies: [],
        upload_status: '',
      };
      return [baseCompany, listEmailCompany];
    }
    return [baseCompany];
  });
};

export const createUploadItems = (scoringGrid: IScoringGridCompany[]): IScoredUpload[] => {
  const proposedItems = scoringGrid
    .map((scoringGridCompany) => ({
      id: scoringGridCompany.id,
      email: scoringGridCompany.email.toLowerCase().trim(),
      ...(scoringGridCompany.target_user_id === undefined ? {} : { target_user_id: scoringGridCompany.target_user_id }),
      company_code: parseCompanyCode(scoringGridCompany.selected_company).toLowerCase().trim(),
      list_name: scoringGridCompany.list_name,
    }))
    .filter((scoredUpload) => scoredUpload.email.length > 0 && scoredUpload.company_code.length > 0);

  return proposedItems.filter(
    (record, index, self) =>
      index ===
      self.findIndex(
        (item) =>
          item.email === record.email &&
          item.target_user_id === record.target_user_id &&
          item.company_code === record.company_code &&
          item.list_name === record.list_name
      )
  );
};

const lodashStyleGet = (myArray: IAlternativeCompany[], index: number) => {
  return myArray.length > index ? (myArray[index] ?? '') : '';
};

export const exportCSV = (scoringGrid: IScoringGridCompany[]) => {
  const toExport = scoringGrid.map((scoreGridCompany: IScoringGridCompany) => ({
    id: scoreGridCompany.id,
    email: scoreGridCompany.email,
    fcode: scoreGridCompany.fcode,
    ticker_symbol: scoreGridCompany.ticker_symbol,
    user_company: scoreGridCompany.user_company,
    country: scoreGridCompany.country,
    selected_name: parseCompanyName(scoreGridCompany.selected_company),
    alt1_name: parseCompanyName(displayCompanyIfNotDeleteMe(lodashStyleGet(scoreGridCompany.alternative_companies, 0))),
    alt2_name: parseCompanyName(displayCompanyIfNotDeleteMe(lodashStyleGet(scoreGridCompany.alternative_companies, 1))),
    alt3_name: parseCompanyName(displayCompanyIfNotDeleteMe(lodashStyleGet(scoreGridCompany.alternative_companies, 2))),
    selected_code: parseCompanyCode(scoreGridCompany.selected_company),
    alt1_code: parseCompanyCode(displayCompanyIfNotDeleteMe(lodashStyleGet(scoreGridCompany.alternative_companies, 0))),
    alt2_code: parseCompanyCode(displayCompanyIfNotDeleteMe(lodashStyleGet(scoreGridCompany.alternative_companies, 1))),
    alt3_code: parseCompanyCode(displayCompanyIfNotDeleteMe(lodashStyleGet(scoreGridCompany.alternative_companies, 2))),
    selected_country: parseCompanyCountry(scoreGridCompany.selected_company),
    alt1_country: parseCompanyCountry(displayCompanyIfNotDeleteMe(lodashStyleGet(scoreGridCompany.alternative_companies, 0))),
    alt2_country: parseCompanyCountry(displayCompanyIfNotDeleteMe(lodashStyleGet(scoreGridCompany.alternative_companies, 1))),
    alt3_country: parseCompanyCountry(displayCompanyIfNotDeleteMe(lodashStyleGet(scoreGridCompany.alternative_companies, 2))),
    upload_status: scoreGridCompany.upload_status,
  }));
  return Papa.unparse(toExport, { quotes: true });
};
